import React from 'react';
import { useEditor, useEditorState, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

/**
 * Quote description editor (admin). WYSIWYG, stored as Markdown: only Markdown-expressible
 * formatting (no underline / colours / sizes).
 */

export const QUOTE_DESCRIPTION_MAX = 10000;

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

// "example.hu" -> https://, "a@b.hu" -> mailto:
const normalizeUrl = (raw: string): string => {
    const url = raw.trim();
    if (url === '' || SAFE_URL.test(url)) return url;
    if (/^[^\s@/]+@[^\s@]+$/.test(url)) return 'mailto:' + url;
    return 'https://' + url;
};

interface Props {
    value: string;
    onChange: (markdown: string) => void;
    disabled?: boolean;
}

const ToolButton: React.FC<{ title: string; active?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }> =
    ({ title, active, disabled, onClick, children }) => (
        <Tooltip title={title}>
            <span>
                <IconButton size="small" disabled={disabled}
                    onMouseDown={(e) => e.preventDefault()} onClick={onClick}
                    // Theme IconButton is white (dark surfaces); && beats the MUI state rules
                    sx={{
                        borderRadius: 1,
                        '&&': { color: active ? 'primary.main' : '#1a1a1a', bgcolor: active ? 'rgba(62,92,75,0.15)' : 'transparent' },
                        '&&:hover': { bgcolor: 'rgba(0,0,0,0.06)', color: active ? 'primary.main' : '#1a1a1a' },
                        '&&.Mui-disabled': { color: 'rgba(0,0,0,0.26)' },
                    }}>
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );

const LinkDialog: React.FC<{ editor: Editor; open: boolean; onClose: () => void }> = ({ editor, open, onClose }) => {
    const [url, setUrl] = React.useState('');
    React.useEffect(() => {
        if (open) setUrl(editor.getAttributes('link').href ?? '');
    }, [open, editor]);

    const apply = () => {
        const href = normalizeUrl(url);
        const chain = editor.chain().focus().extendMarkRange('link');
        if (href === '') {
            chain.unsetLink().run();
        } else if (editor.state.selection.empty && !editor.isActive('link')) {
            chain.insertContent({ type: 'text', text: url.trim(), marks: [{ type: 'link', attrs: { href } }] }).run();
        } else {
            chain.setLink({ href }).run();
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Link</DialogTitle>
            <DialogContent>
                <TextField autoFocus fullWidth size="small" margin="dense" label="URL vagy e-mail" value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
                    helperText="Üresen a link törlődik." />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Mégse</Button>
                <Button variant="contained" onClick={apply}>Rendben</Button>
            </DialogActions>
        </Dialog>
    );
};

const QuoteDescriptionEditor: React.FC<Props> = ({ value, onChange, disabled }) => {
    // Last Markdown emitted by the editor; outside value changes (prefill/reset) are pushed in
    const emitted = React.useRef(value);
    const [linkOpen, setLinkOpen] = React.useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                code: false,
                codeBlock: false,
                underline: false,
                link: {
                    openOnClick: false,
                    autolink: true,
                    defaultProtocol: 'https',
                    isAllowedUri: (url, ctx) => SAFE_URL.test(url) && ctx.defaultValidate(url),
                },
            }),
            Markdown,
        ],
        content: value,
        contentType: 'markdown',
        editable: !disabled,
        onUpdate: ({ editor }) => {
            const md = editor.isEmpty ? '' : editor.getMarkdown();
            emitted.current = md;
            onChange(md);
        },
    });

    React.useEffect(() => {
        if (value !== emitted.current) {
            emitted.current = value;
            editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
        }
    }, [value, editor]);

    React.useEffect(() => { editor.setEditable(!disabled); }, [disabled, editor]);

    const s = useEditorState({
        editor,
        selector: ({ editor: e }) => ({
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            strike: e.isActive('strike'),
            h2: e.isActive('heading', { level: 2 }),
            h3: e.isActive('heading', { level: 3 }),
            bullet: e.isActive('bulletList'),
            ordered: e.isActive('orderedList'),
            quote: e.isActive('blockquote'),
            link: e.isActive('link'),
            canUndo: e.can().undo(),
            canRedo: e.can().redo(),
        }),
    });

    const run = (fn: (c: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>) => () => { fn(editor.chain().focus()).run(); };
    const tooLong = value.length > QUOTE_DESCRIPTION_MAX;

    return (
        <Box>
            <Box sx={{ border: 1, borderColor: tooLong ? 'error.main' : 'grey.400', borderRadius: 1, bgcolor: '#fff', opacity: disabled ? 0.6 : 1 }}>
                <Stack direction="row" flexWrap="wrap" alignItems="center"
                    sx={{ px: 0.5, py: 0.25, borderBottom: 1, borderColor: 'grey.300', bgcolor: 'grey.50', '& .MuiDivider-root': { borderColor: 'grey.300' } }}>
                    <ToolButton title="Félkövér" active={s.bold} disabled={disabled} onClick={run(c => c.toggleBold())}><FormatBoldIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Dőlt" active={s.italic} disabled={disabled} onClick={run(c => c.toggleItalic())}><FormatItalicIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Áthúzott" active={s.strike} disabled={disabled} onClick={run(c => c.toggleStrike())}><StrikethroughSIcon fontSize="small" /></ToolButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <ToolButton title="Címsor" active={s.h2} disabled={disabled} onClick={run(c => c.toggleHeading({ level: 2 }))}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', width: 20, color: 'inherit' }}>H2</Typography>
                    </ToolButton>
                    <ToolButton title="Alcím" active={s.h3} disabled={disabled} onClick={run(c => c.toggleHeading({ level: 3 }))}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', width: 20, color: 'inherit' }}>H3</Typography>
                    </ToolButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <ToolButton title="Felsorolás" active={s.bullet} disabled={disabled} onClick={run(c => c.toggleBulletList())}><FormatListBulletedIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Számozott lista" active={s.ordered} disabled={disabled} onClick={run(c => c.toggleOrderedList())}><FormatListNumberedIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Idézet" active={s.quote} disabled={disabled} onClick={run(c => c.toggleBlockquote())}><FormatQuoteIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Link" active={s.link} disabled={disabled} onClick={() => setLinkOpen(true)}><LinkIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Elválasztó vonal" disabled={disabled} onClick={run(c => c.setHorizontalRule())}><HorizontalRuleIcon fontSize="small" /></ToolButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <ToolButton title="Formázás törlése" disabled={disabled} onClick={run(c => c.unsetAllMarks().clearNodes())}><FormatClearIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Visszavonás" disabled={disabled || !s.canUndo} onClick={run(c => c.undo())}><UndoIcon fontSize="small" /></ToolButton>
                    <ToolButton title="Újra" disabled={disabled || !s.canRedo} onClick={run(c => c.redo())}><RedoIcon fontSize="small" /></ToolButton>
                </Stack>
                <Box sx={{
                    typography: 'body2',
                    color: '#1a1a1a',
                    '& .ProseMirror': { minHeight: 140, maxHeight: 360, overflowY: 'auto', p: 1.5, outline: 'none' },
                    '& .ProseMirror p': { my: 0.5 },
                    '& .ProseMirror h2': { fontSize: '1.1em', my: 1 },
                    '& .ProseMirror h3': { fontSize: '1em', my: 1 },
                    '& .ProseMirror ul, & .ProseMirror ol': { pl: 3, my: 0.5 },
                    '& .ProseMirror blockquote': { m: 0, pl: 1.5, borderLeft: 3, borderColor: 'grey.400', color: '#555' },
                    '& .ProseMirror a': { color: 'primary.main', textDecoration: 'underline' },
                    '& .ProseMirror hr': { border: 0, borderTop: 1, borderColor: 'grey.400', my: 1.5 },
                }}>
                    <EditorContent editor={editor} />
                </Box>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.25, color: tooLong ? 'error.main' : 'text.secondary' }}>
                {value.length} / {QUOTE_DESCRIPTION_MAX}
            </Typography>
            <LinkDialog editor={editor} open={linkOpen} onClose={() => setLinkOpen(false)} />
        </Box>
    );
};

export default QuoteDescriptionEditor;
